<template>
  <ContentPage>
    <template #header>
      <ParticipantBriefSkeleton v-if="isLoading" />
      <ParticipantBrief v-else-if="participant" :participant="participant" />
    </template>

    <!-- Check Ins -->
    <ContentSection title="Check-ins" empty-text="No check-ins">
      <CheckInItemSkeleton v-if="isLoading" />
      <CheckInItem
        v-for="checkIn in eventCheckIns"
        v-else
        :key="checkIn.id"
        :check-in="checkIn"
        :model-value="isCheckedIn(checkIn.id)"
        @update:model-value="onCheckinUpdate($event, checkIn)"
      />
    </ContentSection>

    <!-- Personal Manager -->
    <ContentSection title="Personal Manager" empty-text="No personal manager assigned">
      <ContentCardSimple v-if="isLoading || participant?.personal_manager" dense>
        <ParticipantBriefSkeleton v-if="isLoading" />
        <ParticipantBrief
          v-else-if="participant?.personal_manager"
          :participant="participant.personal_manager"
        />
      </ContentCardSimple>
    </ContentSection>

    <!-- Company -->
    <ContentSection title="Company" empty-text="No company linked">
      <ContentCardSimple v-if="isLoading || participant?.company" dense>
        <CompanyBriefSkeleton v-if="isLoading" />
        <CompanyBrief v-else-if="participant?.company" :company="participant.company" />
      </ContentCardSimple>
    </ContentSection>

    <!-- Business Program -->
    <ContentSection v-if="program?.data" title="Business Programme">
      <template v-for="item in program?.data" :key="item.id">
        <BusinessProgramItemCard v-if="item.type !== 'session'" :item="item" />
        <template v-for="topic in getParticipantTopics(item)" v-else :key="topic.id">
          <BusinessProgramItemCard :item="item" :topic="topic" />
        </template>
      </template>
    </ContentSection>
  </ContentPage>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { isAxiosError } from 'axios'
import { useParticipantQuery } from '@/queries/participants'
import { useCheckInParticipantMutation, useUnCheckInParticipantMutation } from '@/queries/checkin'
import { useCurrentEvent } from '@/composables/current-event'
import type { CheckIn } from '@/types/checkin'
import { useBusinessProgramQuery } from '@/queries/business-program'
import type { BusinessProgramItem } from '@/types'

const route = useRoute('delegate')
const router = useRouter()

const { data: currentEvent } = useCurrentEvent()
const eventCheckIns = computed(() => currentEvent.value?.check_ins ?? [])

const checkedInIds = ref<Set<string>>(new Set())

const { data: participant, isLoading, error } = useParticipantQuery(route.params.delegate_id)
const { data: program } = useBusinessProgramQuery({
  filter: { participant_id: route.params.delegate_id },
})
const { mutateAsync: checkInParticipant } = useCheckInParticipantMutation(route.params.delegate_id)
const { mutateAsync: unCheckInParticipant } = useUnCheckInParticipantMutation(
  route.params.delegate_id,
)

// Watchers
watch(error, (err) => {
  if (isAxiosError(err) && err.response?.status === 404) {
    router.replace({ name: 'companies' })
  }
})

watch(
  () => participant.value?.check_ins,
  (checkIns) => {
    checkedInIds.value = new Set((checkIns ?? []).map((c) => c.id))
  },
  { immediate: true },
)

// Methods
function getParticipantTopics(item: BusinessProgramItem) {
  if (item.type !== 'session') {
    return []
  }

  return (item.session?.topics || []).filter((topic) => {
    return topic.speakers.some((id) => id === route.params.delegate_id)
  })
}

function isCheckedIn(checkInId: string): boolean {
  return checkedInIds.value.has(checkInId)
}

function onCheckinUpdate(value: boolean, checkIn: CheckIn) {
  if (value) {
    checkedInIds.value.add(checkIn.id)
    checkInParticipant(checkIn.id).catch((error) => {
      checkedInIds.value.delete(checkIn.id)
      return Promise.reject(error)
    })
  } else {
    checkedInIds.value.delete(checkIn.id)
    unCheckInParticipant(checkIn.id).catch((error) => {
      checkedInIds.value.add(checkIn.id)
      return Promise.reject(error)
    })
  }
}

// extend route page
definePage({
  name: 'delegate',
  meta: {
    title: 'Delegate',
    hideNav: true,
  },
})
</script>
